/**
 * Unit-scoped inventory room filter (feature/inventory-room-filter).
 *
 * A room always belongs to a unit, so the room filter only makes sense once a
 * unit is selected. Matching is done EXCLUSIVELY on assignment.room_id — never
 * on room_name and never via string parsing. room_id === null means the item is
 * shared / assigned to the whole flat ("Gemeinschaftlich genutzt"), which is a
 * distinct concept from "free" / "unassigned" / "stored".
 *
 * Assignment shape (from GET /admin/units/{id}/inventory-assignments →
 * inventory_service._assignment_to_dict): { inventory_item_id, room_id,
 * quantity, room_name, ... }. room_name is used only as a display label.
 */

export const ROOM_FILTER_ALL = "all";
export const ROOM_FILTER_SHARED = "__shared__";

/**
 * Does one assignment belong to the selected room? room_id is the only key.
 * "__shared__" matches ONLY an explicit room_id === null (whole-flat / shared);
 * a missing field or undefined does NOT count as shared — an unknown room is not
 * a shared room. room_name is never used for matching.
 */
export function matchesInventoryRoom(assignment, roomFilterId) {
  if (!roomFilterId || roomFilterId === ROOM_FILTER_ALL) return true;
  const rawRoomId = assignment ? assignment.room_id : undefined;
  if (roomFilterId === ROOM_FILTER_SHARED) return rawRoomId === null;
  return rawRoomId != null && String(rawRoomId) === String(roomFilterId);
}

/** { inventory_item_id → summed quantity } across assignments matching the room. */
export function buildRoomQuantityByItemId(assignments, roomFilterId) {
  const out = {};
  (Array.isArray(assignments) ? assignments : []).forEach((a) => {
    if (!matchesInventoryRoom(a, roomFilterId)) return;
    const itemId = a && a.inventory_item_id != null ? String(a.inventory_item_id) : "";
    if (!itemId) return;
    out[itemId] = (out[itemId] || 0) + Number((a && a.quantity) || 0);
  });
  return out;
}

/** Set of inventory_item_id whose assignments match the selected room. */
export function matchingRoomItemIds(assignments, roomFilterId) {
  const set = new Set();
  (Array.isArray(assignments) ? assignments : []).forEach((a) => {
    if (!matchesInventoryRoom(a, roomFilterId)) return;
    if (a && a.inventory_item_id != null) set.add(String(a.inventory_item_id));
  });
  return set;
}

/**
 * Filter an inventory item list to the selected room and annotate each surviving
 * row with room_quantity (summed quantity in that room). roomFilterId "all"
 * (or falsy) returns the list unchanged (no room_quantity added).
 */
export function applyInventoryRoomFilter(items, assignments, roomFilterId) {
  const list = Array.isArray(items) ? items : [];
  if (!roomFilterId || roomFilterId === ROOM_FILTER_ALL) return list.slice();
  const ids = matchingRoomItemIds(assignments, roomFilterId);
  const qty = buildRoomQuantityByItemId(assignments, roomFilterId);
  return list
    .filter((it) => ids.has(String(it.id)))
    .map((it) => ({ ...it, room_quantity: qty[String(it.id)] ?? 0 }));
}

/**
 * Build the room dropdown options for a unit.
 *  - "Alle Räume" always first.
 *  - "Gemeinschaftlich genutzt" only if ≥1 assignment has room_id === null.
 *  - Concrete rooms from fetchAdminRooms(unitId) when available (label carries
 *    size); otherwise derived from assignments (room_id = key, room_name = label
 *    only).
 */
export function buildRoomOptions(assignments, rooms) {
  const list = Array.isArray(assignments) ? assignments : [];
  const options = [{ value: ROOM_FILTER_ALL, label: "Alle Räume" }];

  // Shared only for an explicit room_id === null — never undefined / missing field.
  const hasShared = list.some((a) => a != null && a.room_id === null);
  if (hasShared) {
    options.push({ value: ROOM_FILTER_SHARED, label: "Gemeinschaftlich genutzt" });
  }

  const roomList = Array.isArray(rooms) ? rooms : [];
  if (roomList.length) {
    roomList.forEach((r) => {
      const id = r.id ?? r.roomId;
      if (id == null) return;
      const name = r.name ?? r.roomName ?? String(id);
      const label = r.size_m2 != null ? `${name} / ${r.size_m2} m²` : String(name);
      options.push({ value: String(id), label });
    });
  } else {
    const seen = new Set();
    list.forEach((a) => {
      if (!a || a.room_id == null) return;
      const id = String(a.room_id);
      if (seen.has(id)) return;
      seen.add(id);
      options.push({ value: id, label: a.room_name ? String(a.room_name) : id });
    });
  }
  return options;
}
